<template>
  <div ui-view="" :class="isPublic ? 'dp-public-page' : 'app-body'" id="view">
    <page-header v-if="!isPublic" title="일일작업허가서" description="일일작업 허가서 상세 내용을 조회합니다." />
    <div :class="isPublic ? 'dp-public-card' : 'page-body ph-30'">
      <div v-if="isPublic" class="dp-public-title">일일작업 허가서</div>
      <div class="wp-check-page">
        <!-- 헤더 -->
        <div class="form-group row">
          <label class="col-2 form-control-label"><span>일일허가서 번호</span></label>
          <div class="col-10 pt-2">
            <span style="font-weight:600;">{{ dailyPermitId || '-' }}</span>
            <span class="tag ml-10" :class="statusTagClass(record && record.status)">{{ statusLabel(record && record.status) }}</span>
            <span class="text-888 ml-10">(부모 작업허가서: <strong>{{ record && record.parentPlanId || '-' }}</strong>)</span>
          </div>
        </div>

        <div v-if="loading" class="text-center text-888 pv-30">불러오는 중...</div>
        <div v-else-if="!record" class="text-center text-888 pv-30">일일작업허가서를 찾을 수 없습니다.</div>

        <template v-else>
        <!-- 작업 기본 정보 -->
        <div class="form-group row mt-20">
          <label class="col-2 form-control-label"><span>작업 기본 정보</span></label>
          <div class="col-10 pt-2">
            <div class="result-tbl">
              <table class="tbl">
                <colgroup><col width="140px"><col width="auto"><col width="140px"><col width="auto"></colgroup>
                <tbody>
                  <tr>
                    <th>업체명</th><td>{{ basic.companyName || record.companyName || '-' }}</td>
                    <th>작업일자</th><td>{{ formatDay(record.workDate) }}</td>
                  </tr>
                  <tr>
                    <th>작업 책임자</th><td>{{ basic.workManager || '-' }} <span class="text-888 ml-10">{{ basic.workManagerPhone || '' }}</span></td>
                    <th>안전담당자</th><td>{{ basic.safetyManager || '-' }} <span class="text-888 ml-10">{{ basic.safetyManagerPhone || '' }}</span></td>
                  </tr>
                  <tr>
                    <th>작업시간</th><td>{{ timeText }}</td>
                    <th>작업인원</th><td>{{ basic.workPeopleCount != null ? basic.workPeopleCount + '명' : '-' }}</td>
                  </tr>
                  <tr>
                    <th>부모 작업허가서</th><td>{{ record.parentPlanId || '-' }}</td>
                    <th>작성/제출일</th><td>{{ formatTs(record.submittedDate || record.createDate) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- 작업 위치 -->
        <div class="form-group row mt-20" v-if="parsed.locationText || parsed.locationDetailText">
          <label class="col-2 form-control-label"><span>작업 위치</span></label>
          <div class="col-10 pt-2">
            <div>{{ parsed.locationText || '-' }}</div>
            <div class="mt-10" v-if="parsed.locationDetailText" style="white-space:pre-wrap;">
              <strong>상세위치:</strong> {{ parsed.locationDetailText }}
            </div>
          </div>
        </div>

        <!-- 작업 종류 -->
        <div class="form-group row mt-20" v-if="types.length">
          <label class="col-2 form-control-label"><span>작업 종류</span></label>
          <div class="col-10 pt-2">
            <span v-for="t in types" :key="t.key" class="tag mr-6" :class="t.kind === 'high' ? 'light-blue' : 'warning'">{{ t.label }}</span>
          </div>
        </div>

        <!-- 개인보호구 -->
        <div class="form-group row mt-20" v-if="protections.length">
          <label class="col-2 form-control-label"><span>개인보호구</span></label>
          <div class="col-10 pt-2">
            <span v-for="(p, i) in protections" :key="'prot-'+i" class="tag line-type mr-6">{{ protLabel(p) }}</span>
          </div>
        </div>

        <!-- 작업단계별 위험성평가 -->
        <div class="form-group row mt-20" v-if="riskFlat.length">
          <label class="col-2 form-control-label"><span>작업단계별 위험성평가</span></label>
          <div class="col-10 pt-2">
            <table class="tbl">
              <colgroup><col width="140px"><col><col width="110px"><col><col width="70px"><col></colgroup>
              <thead>
                <tr><th>작업항목</th><th>작업절차</th><th>위험요인</th><th>현재 안전조치</th><th>위험도</th><th>추가 안전조치</th></tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in riskFlat" :key="'risk-'+i">
                  <td>{{ row.item || '-' }}</td>
                  <td>{{ row.procedure || '-' }}</td>
                  <td>{{ row.hazard || '-' }}</td>
                  <td>{{ row.currentSafety || '-' }}</td>
                  <td>
                    <span v-if="row.riskLevel" class="tag" :class="riskChipClass(row.riskLevel)">{{ row.riskLevel }}</span>
                    <span v-else>-</span>
                  </td>
                  <td>{{ row.additionalSafety || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 작업자 명단 -->
        <div class="form-group row mt-20" v-if="workers.length">
          <label class="col-2 form-control-label"><span>작업자 명단 ({{ workers.length }}명)</span></label>
          <div class="col-10 pt-2">
            <span v-for="(w, i) in workers" :key="'wk-'+i" class="tag line-type mr-6">{{ w.name || '-' }}</span>
          </div>
        </div>

        <!-- 작업종류별 상세 양식 (탭) -->
        <div class="form-group row mt-20" v-if="formEntries.length">
          <label class="col-2 form-control-label"><span>작업종류별 상세 양식</span></label>
          <div class="col-10 pt-2">
            <ul class="nav nav-tabs wp-hr-tabs">
              <li class="nav-item" v-for="fe in formEntries" :key="'tab-'+fe.key">
                <a href="javascript:void(0)" :class="{ active: activeFormKey === fe.key }" @click="activeFormKey = fe.key">{{ fe.label }}</a>
              </li>
            </ul>
            <div class="tab-content wp-detail-tab-content" v-if="activeForm">
              <permit-type-summary :perm-key="activeForm.permKey" :data="activeForm.data"></permit-type-summary>
            </div>
          </div>
        </div>
        </template>

        <!-- 액션 버튼 -->
        <div class="btn-wrap mt-30 justify-content-center">
          <button v-if="!isPublic" class="btn btn-lg line-2" @click="goBack">
            <i class="icon-arrow-line-left mr-5"></i>목록으로
          </button>
          <!-- 엑셀 다운로드 / PDF 다운v2 는 숨김 (기획 요청). 핸들러·API 는 유지 — 되돌릴 때 v-if 만 해제 -->
          <button v-if="false" class="btn btn-lg line-2" :disabled="!parsed" @click="downloadExcel">
            <i class="icon-download mr-5"></i>엑셀 다운로드
          </button>
          <button v-if="false" class="btn btn-lg line-2" :disabled="!parsed || pdfV2Loading" @click="downloadPdfV2">
            <i class="icon-download mr-5"></i>{{ pdfV2Loading ? 'PDF v2 생성 중...' : 'PDF 다운v2' }}
          </button>
          <button class="btn btn-lg blue" :disabled="!parsed || pdfLoading" @click="downloadPdf">
            <i class="icon-download mr-5"></i>{{ pdfLoading ? 'PDF 생성 중...' : 'PDF 다운로드' }}
          </button>
          <button class="btn btn-lg blue" :disabled="!parsed || !hasChecklist"
                  :title="hasChecklist ? '' : '선택된 고위험·중장비 작업이 없어 해당 점검표가 없습니다.'"
                  @click="downloadChecklist">
            <i class="icon-download mr-5"></i>점검표 다운로드
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import PageHeader from '@/layout/PageHeader.vue';
import PermitTypeSummary from '@/views/app/workPermit/components/PermitTypeSummary.vue';

// 일일작업허가서 작업종류 라벨 → 안전작업허가서 permit payload key
// (상세 양식을 안전작업허가서와 동일한 컴포넌트로 렌더링하기 위한 매핑)
const LABEL_TO_KEY = {
  '고소작업': 'highAltitudePermit',
  '화기작업': 'fireWorkPermit',
  '밀폐공간': 'confinedSpacePermit',
  '화학물질': 'chemicalPermit',
  '전기작업': 'electricWorkPermit',
  '중량물취급': 'heavyLiftPermit',
  '운전중작업': 'operatingWorkPermit',
  '굴착작업': 'excavationPermit',
  '차량계하역운반기계': 'cargoEquipPermit',
  '이동식크레인작업': 'mobileCranePermit',
  '차량계건설기계 작업': 'constrEquipPermit',
  '지게차': 'forkliftPermit',
  '차량형 고소작업대': 'boomLiftPermit',
  '시저형 고소작업대': 'scissorLiftPermit'
};

export default {
  name: 'DailyPermitDetail',
  components: { PageHeader, PermitTypeSummary },
  data() {
    return {
      dailyPermitId: '',
      record: null,
      parsed: null,
      loading: false,
      pdfLoading: false,
      pdfV2Loading: false,
      activeFormKey: ''
    };
  },
  computed: {
    // 외부(비로그인) 공개 조회 페이지 여부 — 라우트로 구분. standalone 레이아웃 + 목록 버튼 숨김.
    isPublic() { return this.$route.name === 'dailyPermitView'; },
    common() { return (this.parsed && this.parsed.common) || {}; },
    basic() { return this.common.basic || {}; },
    types() { return (this.parsed && this.parsed.types) || []; },
    /**
     * 점검표 대상 존재 여부. 고위험·중장비 작업종류가 하나도 없으면 점검표 시트도 없다.
     * (BE CHECKLIST_SHEET 는 14종 라벨을 모두 매핑하므로 types 가 비었을 때만 0건)
     * 눌러서 실패를 보기 전에 버튼 단계에서 막는다.
     */
    hasChecklist() { return this.types.length > 0; },
    protections() { return this.common.protections || []; },
    workers() { return this.common.workers || []; },
    timeText() {
      const ts = (this.common.timeSlots || []).join(', ');
      const from = this.common.workFromTime;
      const to = this.common.workToTime;
      const range = (from || to) ? `${from || ''}~${to || ''}` : '';
      return [ts, range].filter(Boolean).join(' / ') || '-';
    },
    riskFlat() {
      const out = [];
      (this.common.riskRows || []).forEach((row) => {
        const sets = row.sets || [];
        if (!sets.length) { out.push({ item: row.item }); return; }
        sets.forEach((s) => out.push({
          item: row.item, procedure: s.procedure, hazard: s.hazard,
          currentSafety: s.currentSafety, riskLevel: s.riskLevel, additionalSafety: s.additionalSafety
        }));
      });
      return out;
    },
    formEntries() {
      const forms = (this.parsed && this.parsed.forms) || {};
      return Object.keys(forms).map((key) => {
        const label = this.typeLabel(key);
        return {
          key,
          label,
          // 안전작업허가서 상세와 동일한 permit 컴포넌트로 렌더링하기 위한 매핑 키
          permKey: LABEL_TO_KEY[label] || key,
          data: forms[key] || {}
        };
      });
    },
    activeForm() {
      return this.formEntries.find((f) => f.key === this.activeFormKey) || this.formEntries[0] || null;
    }
  },
  mounted() {
    this.dailyPermitId = this.$route.query.dailyPermitId || '';
    if (this.dailyPermitId) this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        // 외부 공개 페이지는 매직링크 토큰으로 인증하는 shared 엔드포인트 사용
        const url = this.isPublic
          ? '/api/public/daily-permit/shared/' + encodeURIComponent(this.dailyPermitId)
            + '?token=' + encodeURIComponent(this.$route.query.token || '')
          : '/api/private/daily-permit/' + encodeURIComponent(this.dailyPermitId);
        const res = await this.$http.get(url);
        const data = res && res.data ? (res.data.payload || res.data) : null;
        this.record = data || null;
        try {
          this.parsed = this.record && this.record.payload ? JSON.parse(this.record.payload) : {};
        } catch (e) {
          this.parsed = {};
        }
        const keys = Object.keys((this.parsed && this.parsed.forms) || {});
        this.activeFormKey = keys[0] || '';
      } catch (e) {
        console.log('daily-permit 상세 조회 실패', e);
        this.record = null;
      } finally {
        this.loading = false;
      }
    },
    downloadExcel() {
      if (!this.parsed) return;
      const body = {
        region: this.parsed.region || (this.record && this.record.idcCode && String(this.record.idcCode).includes('춘천') ? '춘천' : '세종'),
        workDate: this.formatDay(this.record.workDate),
        locationText: this.parsed.locationText,
        locationDetailText: this.parsed.locationDetailText,
        common: this.parsed.common,
        types: this.parsed.types,
        forms: this.parsed.forms
      };
      this.$http.post('/api/public/daily-permit/excel', body, { responseType: 'blob' })
        .then((result) => {
          const url = window.URL.createObjectURL(new Blob([result.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `일일작업허가서_${this.dailyPermitId}.xlsx`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((e) => {
          console.log('엑셀 다운로드 실패', e);
          this.noti('다운로드 실패', '엑셀 생성에 실패했습니다.', 'danger');
        });
    },
    // 화면 내용을 그대로 담은 PDF(zip: 통합본 + 양식별 + 첨부파일) 다운로드
    downloadPdf() {
      if (!this.parsed || this.pdfLoading) return;
      this.pdfLoading = true;
      const body = {
        parentPlanId: this.record && this.record.parentPlanId,
        region: this.parsed.region || (this.record && this.record.idcCode && String(this.record.idcCode).includes('춘천') ? '춘천' : '세종'),
        workDate: this.formatDay(this.record.workDate),
        locationText: this.parsed.locationText,
        locationDetailText: this.parsed.locationDetailText,
        common: this.parsed.common,
        types: this.parsed.types,
        forms: this.parsed.forms
      };
      this.$http.post('/api/public/daily-permit/pdf', body, { responseType: 'blob' })
        .then((result) => {
          const url = window.URL.createObjectURL(new Blob([result.data], { type: 'application/zip' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `일일작업허가서_${this.dailyPermitId}.zip`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((e) => {
          console.log('PDF 다운로드 실패', e);
          this.noti('다운로드 실패', 'PDF 생성에 실패했습니다.', 'danger');
        })
        .finally(() => {
          this.pdfLoading = false;
        });
    },
    // PDF v2: 엑셀 서식을 그대로 PDF 로 변환(글자 잘림 없음) — 단일 PDF
    downloadPdfV2() {
      if (!this.parsed || this.pdfV2Loading) return;
      this.pdfV2Loading = true;
      const body = {
        region: this.parsed.region || (this.record && this.record.idcCode && String(this.record.idcCode).includes('춘천') ? '춘천' : '세종'),
        workDate: this.formatDay(this.record.workDate),
        locationText: this.parsed.locationText,
        locationDetailText: this.parsed.locationDetailText,
        common: this.parsed.common,
        types: this.parsed.types,
        forms: this.parsed.forms
      };
      this.$http.post('/api/public/daily-permit/pdf-v2', body, { responseType: 'blob' })
        .then((result) => {
          const url = window.URL.createObjectURL(new Blob([result.data], { type: 'application/pdf' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `일일작업허가서_${this.dailyPermitId}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((e) => {
          console.log('PDF v2 다운로드 실패', e);
          this.noti('다운로드 실패', 'PDF v2 생성에 실패했습니다.', 'danger');
        })
        .finally(() => {
          this.pdfV2Loading = false;
        });
    },
    downloadChecklist() {
      if (!this.parsed) return;
      const body = {
        region: this.parsed.region || (this.record && this.record.idcCode && String(this.record.idcCode).includes('춘천') ? '춘천' : '세종'),
        workDate: this.formatDay(this.record.workDate),
        locationText: this.parsed.locationText,
        locationDetailText: this.parsed.locationDetailText,
        common: this.parsed.common,
        types: this.parsed.types,
        forms: this.parsed.forms
      };
      // 점검표는 PDF 로 내려받는다. 서버가 기존 엑셀 생성 로직을 그대로 태운 뒤 변환만 덧붙인다.
      this.$http.post('/api/public/daily-permit/checklist/pdf', body, { responseType: 'blob' })
        .then((result) => {
          const url = window.URL.createObjectURL(new Blob([result.data], { type: 'application/pdf' }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `안전조치요구사항_점검표_${this.dailyPermitId}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((e) => {
          // 400 = 해당 점검표 없음(정상 상태) → 안내로 표시. 그 외에만 오류로 처리.
          const status = e && e.response && e.response.status;
          if (status === 400) {
            this.noti('점검표 없음', '선택된 고위험·중장비 작업이 없어 해당 점검표가 없습니다.', 'warning');
            return;
          }
          console.log('점검표 다운로드 실패', e);
          this.noti('다운로드 실패', '점검표 생성에 실패했습니다.', 'danger');
        });
    },
    goBack() {
      if (window.history.length > 1) this.$router.go(-1);
      else this.$router.push({ path: '/app/workPermit/list' });
    },
    protLabel(p) {
      if (!p) return '-';
      return typeof p === 'string' ? p : (p.label || p.name || '-');
    },
    riskChipClass(level) {
      switch (level) {
        case '상': return 'danger';
        case '중': return 'warning';
        case '하': return 'success';
        default: return 'line-type';
      }
    },
    typeLabel(key) {
      const t = (this.types || []).find((x) => x.key === key);
      return (t && t.label) || key;
    },
    formatDay(ms) {
      if (!ms) return '-';
      return moment(Number(ms)).format('YYYY-MM-DD');
    },
    formatTs(ms) {
      if (!ms) return '-';
      return moment(Number(ms)).format('YYYY-MM-DD HH:mm');
    },
    statusLabel(s) {
      switch (s) {
        case 'DRAFT': return '임시저장';
        case 'SUBMITTED': return '검토 대기';
        case 'NTREE_PENDING': return 'NTREE 결재 중';
        case 'APPROVED': return '승인';
        case 'REJECTED': return '반려';
        default: return s || '-';
      }
    },
    statusTagClass(s) {
      switch (s) {
        case 'DRAFT': return 'line-type';
        case 'SUBMITTED': return 'warning';
        case 'NTREE_PENDING': return 'light-blue';
        case 'APPROVED': return 'success';
        case 'REJECTED': return 'danger';
        default: return 'line-type';
      }
    }
  }
};
</script>

<style scoped>
.wp-check-page {
  color: #333;
}
/* 외부(비로그인) 공개 조회 페이지 — LNB/GNB 없는 standalone 카드 */
.dp-public-page {
  display: flex;
  justify-content: center;
  padding: 40px 16px;
  min-height: 100vh;
  background: #f5f7fa;
}
.dp-public-card {
  width: 100%;
  max-width: 1100px;
  background: #fff;
  border: 1px solid #e2e2e2;
  border-radius: 6px;
  padding: 28px 32px;
}
.dp-public-title {
  font-size: 18px;
  font-weight: 700;
  color: #1565c0;
  margin-bottom: 16px;
}
/* 허가서 상세 탭 — NDKC .nav-tabs 기본 스타일 사용, 패널만 패딩 추가 (WorkPermitCheck 동일) */
.wp-detail-tab-content {
  padding-top: 16px;
}
/* 탭이 많아 폭이 모자라면 라벨이 탭 아래로 넘쳐 콘텐츠와 겹치던 문제 → 여러 단으로 줄바꿈, 라벨 한 줄 유지 */
.wp-hr-tabs.nav-tabs {
  flex-wrap: wrap;
  row-gap: 4px;
}
.wp-hr-tabs.nav-tabs .nav-item a {
  white-space: nowrap;
}
.sub-title {
  font-weight: 600;
  font-size: 13px;
  color: #333;
  border-bottom: 1px solid #e2e2e2;
  padding-bottom: 4px;
  margin-bottom: 8px;
}
.mb-10 { margin-bottom: 10px; }
.pv-10 { padding: 10px 0; }
.pv-30 { padding: 30px 0; }
</style>